<template>
  <div class="bg-white rounded-lg shadow-sm border border-secondary-100 overflow-hidden">
    <!-- Header -->
    <div class="p-4 border-b border-secondary-100">
      <div class="flex items-start justify-between">
        <div class="flex items-center gap-3">
          <div 
            class="w-10 h-10 rounded-lg flex items-center justify-center"
            :class="goalIconBg"
          >
            <svg v-if="goal.type === 'revenue'" class="w-5 h-5" :class="goalIconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <svg v-else-if="goal.type === 'contracts'" class="w-5 h-5" :class="goalIconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <svg v-else-if="goal.type === 'units'" class="w-5 h-5" :class="goalIconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
            </svg>
            <svg v-else class="w-5 h-5" :class="goalIconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
            </svg>
          </div>
          <div>
            <h3 class="font-semibold text-secondary-800">{{ goal.name }}</h3>
            <div class="flex items-center gap-2 mt-0.5">
              <span class="text-xs px-2 py-0.5 rounded-full" :class="scopeBadgeClass">
                {{ goal.scope === 'company' ? $t('sales_dashboard.scope_company') : sellerName }}
              </span>
              <span class="text-xs text-secondary-500">{{ periodLabel }}</span>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-1">
          <button 
            @click="$emit('edit', goal)"
            class="p-2 text-secondary-400 hover:text-securdia-blue hover:bg-secondary-50 rounded-lg"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
          </button>
          <button 
            @click="$emit('delete', goal)"
            class="p-2 text-secondary-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Progress Section -->
    <div class="p-4">
      <div class="flex items-end justify-between mb-2">
        <div>
          <span class="text-3xl font-bold" :class="progressColor">{{ currentValue }}</span>
          <span class="text-secondary-400 mx-1">/</span>
          <span class="text-lg text-secondary-600">{{ targetValue }}</span>
        </div>
        <div class="text-right">
          <span class="text-2xl font-bold" :class="progressColor">{{ Math.min(100, progress).toFixed(0) }}%</span>
          <p class="text-xs text-secondary-500">{{ $t('sales_dashboard.achieved') }}</p>
        </div>
      </div>

      <!-- Progress Bar -->
      <div class="h-3 bg-secondary-200 rounded-full overflow-hidden mb-3">
        <div 
          :style="{ width: Math.min(100, progress) + '%' }"
          :class="progressBarColor"
          class="h-full rounded-full transition-all duration-500"
        ></div>
      </div>

      <!-- Stats Row -->
      <div class="grid grid-cols-3 gap-4 pt-3 border-t border-secondary-100">
        <div class="text-center">
          <p class="text-xs text-secondary-500 mb-0.5">{{ $t('sales_dashboard.contracts_count') }}</p>
          <p class="font-semibold text-secondary-800">{{ contractCount }}</p>
        </div>
        <div class="text-center">
          <p class="text-xs text-secondary-500 mb-0.5">{{ $t('sales_dashboard.average') }}</p>
          <p class="font-semibold text-secondary-800">{{ averagePerContract }}</p>
        </div>
        <div class="text-center">
          <p class="text-xs text-secondary-500 mb-0.5">{{ $t('sales_dashboard.remaining_to_goal') }}</p>
          <p class="font-semibold" :class="remaining > 0 ? 'text-securdia-orange' : 'text-green-600'">
            {{ remaining > 0 ? remainingFormatted : $t('sales_dashboard.goal_achieved') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Status Footer -->
    <div 
      class="px-4 py-2 text-sm flex items-center justify-between"
      :class="statusBgColor"
    >
      <div class="flex items-center gap-2">
        <span :class="statusDotColor" class="w-2 h-2 rounded-full"></span>
        <span :class="statusTextColor">{{ statusText }}</span>
      </div>
      <span class="text-xs" :class="statusTextColor">
        {{ goal.type === 'category' ? categoryLabel : typeLabel }}
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BudgetGoalCard',
  props: {
    goal: {
      type: Object,
      required: true
    },
    contracts: {
      type: Array,
      default: () => []
    },
    sellers: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    sellerName() {
      if (this.goal.scope !== 'seller') return '';
      // First try to use stored sellerName, then look up by id
      if (this.goal.sellerName) return this.goal.sellerName;
      const seller = this.sellers.find(s => s.id === this.goal.sellerId);
      return seller?.name || this.$t('sales_dashboard.unknown_seller');
    },

    periodLabel() {
      const year = this.goal.year;
      if (this.goal.period === 'year') return year.toString();
      if (this.goal.period === 'quarter') return `Q${this.goal.quarter} ${year}`;
      
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
      return `${months[this.goal.month - 1]} ${year}`;
    },

    typeLabel() {
      const typeKeys = {
        'revenue': 'goal_type_revenue_label',
        'contracts': 'goal_type_contracts_label',
        'units': 'goal_type_units_label',
        'category': 'goal_type_category_label'
      };
      return this.$t(`sales_dashboard.${typeKeys[this.goal.type]}`) || this.goal.type;
    },

    categoryLabel() {
      const catKeys = {
        'alarm': 'cat_personal_alarm',
        'ajax': 'cat_perimeter',
        'tag': 'cat_tags',
        'display': 'cat_display',
        'vehicle_tracker': 'cat_vehicle_tracker',
        'software': 'cat_software',
        'simcard': 'cat_simcard',
        'accessory': 'cat_accessory'
      };
      return this.$t(`sales_dashboard.${catKeys[this.goal.category]}`) || this.goal.category;
    },

    current() {
      switch (this.goal.type) {
        case 'revenue':
          return this.contracts.reduce((sum, c) => {
            // Use monthlyValue (monthly recurring) + cashValue (one-time) from API
            // Fall back to other possible field names
            const monthly = c.monthlyValue || c.rental_month_cost || c.monthly_value || 0;
            const cash = c.cashValue || c.cash_sum || c.cash_value || 0;
            const total = c.total_value || c.contract_value || 0;
            
            // Use total if available, otherwise sum of monthly and cash
            const revenue = total > 0 ? total : (monthly + cash);
            return sum + revenue;
          }, 0);
        
        case 'contracts':
          return this.contracts.length;
        
        case 'units':
          return this.contracts.reduce((sum, c) => {
            // Use productCount from API or sum from products array
            if (c.productCount) return sum + c.productCount;
            return sum + (c.products || []).reduce((s, p) => s + (p.qty || p.quantity || 1), 0);
          }, 0);
        
        case 'category':
          return this.contracts.reduce((sum, c) => {
            // Filter products by category and sum quantities
            return sum + (c.products || []).filter(p => p.category === this.goal.category)
              .reduce((s, p) => s + (p.qty || p.quantity || 1), 0);
          }, 0);
        
        default:
          return 0;
      }
    },

    currentValue() {
      if (this.goal.type === 'revenue') {
        return this.formatCompact(this.current);
      }
      return this.current.toString();
    },

    targetValue() {
      if (this.goal.type === 'revenue') {
        return this.formatCompact(this.goal.target);
      }
      return this.goal.target.toString();
    },

    progress() {
      if (this.goal.target <= 0) return 0;
      return (this.current / this.goal.target) * 100;
    },

    remaining() {
      return Math.max(0, this.goal.target - this.current);
    },

    remainingFormatted() {
      if (this.goal.type === 'revenue') {
        return this.formatCompact(this.remaining);
      }
      return `${this.remaining} ${this.$t('sales_dashboard.unit_pcs')}`;
    },

    contractCount() {
      return this.contracts.length;
    },

    averagePerContract() {
      if (this.contracts.length === 0) return '-';
      const avg = this.current / this.contracts.length;
      if (this.goal.type === 'revenue') {
        return this.formatCompact(avg);
      }
      return avg.toFixed(1);
    },

    // Styling computed properties
    goalIconBg() {
      const colors = {
        'revenue': 'bg-green-100',
        'contracts': 'bg-blue-100',
        'units': 'bg-purple-100',
        'category': 'bg-orange-100'
      };
      return colors[this.goal.type] || 'bg-secondary-100';
    },

    goalIconColor() {
      const colors = {
        'revenue': 'text-green-600',
        'contracts': 'text-blue-600',
        'units': 'text-purple-600',
        'category': 'text-orange-600'
      };
      return colors[this.goal.type] || 'text-secondary-600';
    },

    scopeBadgeClass() {
      return this.goal.scope === 'company' 
        ? 'bg-securdia-blue/10 text-securdia-blue'
        : 'bg-purple-100 text-purple-700';
    },

    progressColor() {
      if (this.progress >= 100) return 'text-green-600';
      if (this.progress >= 75) return 'text-securdia-blue';
      if (this.progress >= 50) return 'text-securdia-orange';
      if (this.progress >= 25) return 'text-secondary-600';
      return 'text-secondary-500';
    },

    progressBarColor() {
      if (this.progress >= 100) return 'bg-green-500';
      if (this.progress >= 75) return 'bg-securdia-blue';
      if (this.progress >= 50) return 'bg-securdia-orange';
      if (this.progress >= 25) return 'bg-secondary-400';
      return 'bg-secondary-300';
    },

    statusText() {
      if (this.progress >= 100) return this.$t('sales_dashboard.status_achieved');
      if (this.progress >= 75) return this.$t('sales_dashboard.status_almost');
      if (this.progress >= 50) return this.$t('sales_dashboard.status_on_track');
      if (this.progress >= 25) return this.$t('sales_dashboard.status_in_progress');
      return this.$t('sales_dashboard.status_new');
    },

    statusBgColor() {
      if (this.progress >= 100) return 'bg-green-50';
      if (this.progress >= 75) return 'bg-blue-50';
      if (this.progress >= 50) return 'bg-orange-50';
      return 'bg-secondary-50';
    },

    statusDotColor() {
      if (this.progress >= 100) return 'bg-green-500';
      if (this.progress >= 75) return 'bg-securdia-blue';
      if (this.progress >= 50) return 'bg-securdia-orange';
      return 'bg-secondary-400';
    },

    statusTextColor() {
      if (this.progress >= 100) return 'text-green-700';
      if (this.progress >= 75) return 'text-blue-700';
      if (this.progress >= 50) return 'text-orange-700';
      return 'text-secondary-600';
    }
  },
  methods: {
    formatCompact(value) {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M kr`;
      }
      if (value >= 1000) {
        return `${(value / 1000).toFixed(0)}k kr`;
      }
      return `${Math.round(value)} kr`;
    }
  }
};
</script>
